// src/components/SubscriptionPlan.js
import React, { useState, useEffect } from 'react';
import { useRazorpay } from "react-razorpay";
import './SubscriptionPlans.css';
import { getAuthApiToken } from "./../api/tokenService";
import { useAuth } from "./../context/AuthContext";

const SubscriptionPlan = ({ onClose }) => {
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [availablePlans, setAvailablePlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [exchangeRates, setExchangeRates] = useState({});
    const [isLoadingRates, setIsLoadingRates] = useState(true);
    const { user } = useAuth();
    const { Razorpay } = useRazorpay();

    const API_BASE_URL = process.env.REACT_APP_AUTH_URL; //  || 'http://127.0.0.1:3000'

    // Currency symbols mapping
    const currencySymbols = {
        'INR': '₹',
        'USD': '$',
        'EUR': '€',
        'GBP': '£',
        'AED': 'د.إ',
        'SGD': 'S$',
        'CAD': 'C$',
        'AUD': 'A$'
    };

    // Get user's currency from context (default to INR)
    const userCurrency = user?.currency || 'INR';

    // Fetch exchange rates (convert from INR to user's currency)
    useEffect(() => {
        const fetchExchangeRates = async () => {
            try {
                setIsLoadingRates(true);
                // Using API with INR as base currency
                const response = await fetch('https://api.exchangerate-api.com/v4/latest/INR');
                if (response.ok) {
                    const data = await response.json();
                    setExchangeRates(data.rates);
                    console.log('Exchange rates (INR base):', data.rates);
                } else {
                    // Fallback rates (INR to other currencies)
                    setExchangeRates({
                        'INR': 1,
                        'USD': 0.012,
                        'EUR': 0.011,
                        'GBP': 0.0095,
                        'AED': 0.044,
                        'SGD': 0.016,
                        'CAD': 0.016,
                        'AUD': 0.018
                    });
                }
            } catch (error) {
                console.error('Error fetching exchange rates:', error);
                setExchangeRates({
                    'INR': 1,
                    'USD': 0.012,
                    'EUR': 0.011,
                    'GBP': 0.0095,
                    'AED': 0.044,
                    'SGD': 0.016,
                    'CAD': 0.016,
                    'AUD': 0.018
                });
            } finally {
                setIsLoadingRates(false);
            }
        };

        fetchExchangeRates();
    }, []);

    // Listen for currency change events and refresh data
    useEffect(() => {
        const handleCurrencyChange = (event) => {
            console.log('Currency changed to:', event.detail.currency);
            // Refetch data with new currency
            fetchData();
        };

        window.addEventListener('currencyChanged', handleCurrencyChange);

        return () => {
            window.removeEventListener('currencyChanged', handleCurrencyChange);
        };
    }, []);

    // Fetch data when component mounts and when userCurrency changes
    useEffect(() => {
        if (!isLoadingRates) {
            fetchData();
        }
    }, [userCurrency, isLoadingRates]);

    // Convert price from INR to user's currency
    const convertPrice = (priceInINR) => {
        // Convert to number if it's a string
        const numericPrice = typeof priceInINR === 'string' ? parseFloat(priceInINR) : priceInINR;

        // Handle invalid or zero values
        if (isNaN(numericPrice)) return 0;
        if (numericPrice === 0) return 0;

        // If no exchange rates or INR, return original
        if (!exchangeRates || !userCurrency || userCurrency === 'INR') {
            return numericPrice;
        }

        // Get rate from INR to user's currency
        const rate = exchangeRates[userCurrency];
        if (!rate) return numericPrice;

        // Convert and round to 2 decimal places
        const convertedPrice = numericPrice * rate;
        return Math.round(convertedPrice * 100) / 100;
    };

    // Format price with currency symbol
    const formatPrice = (price, currency = userCurrency) => {
        // Handle free plan
        if (price === 0 || price === '0' || price === '0.00') return 'Free';

        // Convert to number if it's a string
        const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

        // Handle invalid numbers
        if (isNaN(numericPrice)) return 'Free';

        const symbol = currencySymbols[currency] || currency;
        const formattedPrice = numericPrice.toFixed(2);

        // Different formats for different currencies
        if (currency === 'INR') {
            return `${symbol}${formattedPrice}`;
        } else if (currency === 'AED') {
            return `${symbol} ${formattedPrice}`;
        } else {
            return `${symbol}${formattedPrice}`;
        }
    };

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const token = await getAuthApiToken();

            // Fetch current subscription
            const subResponse = await fetch(`${API_BASE_URL}/api/auth/subscription/my-subscription`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (subResponse.ok) {
                const subscriptionData = await subResponse.json();

                // Convert current subscription price if needed
                if (subscriptionData?.plan?.price > 0) {
                    const convertedPrice = convertPrice(subscriptionData.plan.price);
                    subscriptionData.plan.convertedPrice = convertedPrice;
                    subscriptionData.plan.formattedPrice = formatPrice(convertedPrice);
                }

                setCurrentSubscription(subscriptionData);
            }

            // Fetch available plans
            const plansResponse = await fetch(`${API_BASE_URL}/api/auth/subscription/plans`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (plansResponse.ok) {
                let plansData = await plansResponse.json();

                // Convert prices to user's currency and add formatted price
                plansData = plansData.map(plan => {
                    const convertedPrice = plan.price === 0 ? 0 : convertPrice(plan.price);

                    return {
                        ...plan,
                        originalPriceINR: plan.price,
                        convertedPrice: convertedPrice,
                        formattedPrice: plan.price === 0 ? 'Free' : formatPrice(convertedPrice),
                        currency: userCurrency
                    };
                });

                setAvailablePlans(plansData);
            }

        } catch (err) {
            console.error('Error fetching data:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUpgrade = async (plan) => {
        if (plan.name === 'free') return;

        setProcessing(true);
        setError(null);
        setSuccess(null);

        try {
            const token = await getAuthApiToken();

            // Send the original plan name (not converted price)
            const response = await fetch(`${API_BASE_URL}/api/auth/subscription/subscribe`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ planName: plan.name })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to upgrade subscription');
            }

            const data = await response.json();

            // Check if payment is required
            if (!data.requiresPayment) {
                alert(data.message);
                await fetchData();
                return;
            }

            // Open Razorpay checkout for paid plan
            const options = {
                key: data.razorpay_key_id,
                amount: data.order.amount,
                currency: data.order.currency,
                name: 'Incident AI',
                description: `Subscribe to ${plan.displayName} Plan`,
                order_id: data.order.id,
                handler: async (paymentResponse) => {
                    const verifyResponse = await fetch(`${API_BASE_URL}/api/auth/subscription/verify-payment`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            razorpay_order_id: paymentResponse.razorpay_order_id,
                            razorpay_payment_id: paymentResponse.razorpay_payment_id,
                            razorpay_signature: paymentResponse.razorpay_signature,
                            subscription_id: data.subscription_id
                        })
                    });

                    const verifyData = await verifyResponse.json();

                    if (verifyResponse.ok) {
                        alert('Payment successful! Your subscription is now active.');
                        await fetchData();
                        onClose?.();
                    } else {
                        alert('Payment verification failed: ' + verifyData.message);
                    }
                },
                prefill: {
                    name: user?.name || '',
                    email: user?.email || ''
                },
                theme: {
                    color: '#7a7a7a'
                },
                modal: {
                    ondismiss: () => {
                        console.log('Checkout closed');
                    }
                }
            };

            const razorpay = new Razorpay(options);
            razorpay.open();

        } catch (err) {
            console.error('Error upgrading:', err);
            setError(err.message);
            setTimeout(() => setError(null), 3000);
        } finally {
            setProcessing(false);
        }
    };

    const handleCancel = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.')) {
            return;
        }

        setProcessing(true);
        setError(null);
        setSuccess(null);

        try {
            const token = await getAuthApiToken();

            const response = await fetch(`${API_BASE_URL}/api/auth/subscription/cancel`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ cancelImmediately: false })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to cancel subscription');
            }

            setSuccess('Your subscription will not renew at the end of the billing period.');

            setTimeout(() => {
                fetchData();
                setSuccess(null);
            }, 2000);

        } catch (err) {
            console.error('Error cancelling:', err);
            setError(err.message);
            setTimeout(() => setError(null), 3000);
        } finally {
            setProcessing(false);
        }
    };

    const isCurrentPlan = (plan) => {
        const currentPlanName = currentSubscription?.plan?.name || 'free';
        return currentPlanName === plan.name;
    };

    // Show loading while fetching exchange rates
    if (isLoadingRates) {
        return (
            <div className="subscription-page-overlay" onClick={onClose}>
                <div className="subscription-page" onClick={(e) => e.stopPropagation()}>
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading currency rates...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="subscription-page-overlay" onClick={onClose}>
                <div className="subscription-page" onClick={(e) => e.stopPropagation()}>
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading subscription details...</p>
                    </div>
                </div>
            </div>
        );
    }

    const sortedPlans = [...availablePlans].sort((a, b) => a.originalPriceINR - b.originalPriceINR);

    return (
        <div className="subscription-page-overlay" onClick={onClose}>
            <div className="subscription-page" onClick={(e) => e.stopPropagation()}>
                <div className="subscription-page-header">
                    <h2>💎 Subscription Management</h2>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                {/* Currency info banner */}
                <div className="currency-info-banner">
                    <span>💱 Prices shown in: {currencySymbols[userCurrency]} {userCurrency}</span>
                    {userCurrency !== 'INR' && (
                        <span className="conversion-note">(Converted from INR using live exchange rates)</span>
                    )}
                </div>

                {error && (
                    <div className="subscription-error">
                        <span>⚠️</span> {error}
                    </div>
                )}

                {success && (
                    <div className="subscription-success">
                        <span>✅</span> {success}
                    </div>
                )}

                {/* Current Subscription Section */}
                <div className="current-plan-section">
                    <h3>Your Current Plan</h3>
                    <div className="current-plan-card">
                        <div className="plan-icon">
                            {currentSubscription?.plan?.name === 'free' ? '🆓' : '💎'}
                        </div>
                        <div className="plan-details">
                            <div className="plan-name">{currentSubscription?.plan?.displayName || 'Free'}</div>
                            {currentSubscription?.plan?.price > 0 && (
                                <div className="plan-price">
                                    {formatPrice(
                                        currentSubscription?.plan?.convertedPrice || currentSubscription?.plan?.price,
                                        userCurrency
                                    )}/{currentSubscription.plan.billingPeriod}
                                </div>
                            )}
                            {currentSubscription?.endDate && currentSubscription?.plan?.name !== 'free' && (
                                <div className="plan-expiry">
                                    📅 Expires: {new Date(currentSubscription.endDate).toLocaleDateString()}
                                </div>
                            )}
                            {currentSubscription?.autoRenew === false && currentSubscription?.plan?.name !== 'free' && (
                                <div className="plan-warning">
                                    ⚠️ Auto-renew is disabled. Your plan will expire on {new Date(currentSubscription.endDate).toLocaleDateString()}
                                </div>
                            )}
                        </div>
                        {currentSubscription?.plan?.name !== 'free' && (
                            <button
                                className="cancel-btn"
                                onClick={handleCancel}
                                disabled={processing}
                            >
                                {processing ? 'Processing...' : 'Cancel Subscription'}
                            </button>
                        )}
                    </div>
                </div>

                {/* Available Plans Section */}
                <div className="available-plans-section">
                    <h3>Available Plans</h3>
                    <div className="plans-grid">
                        {sortedPlans.map(plan => {
                            const isCurrent = isCurrentPlan(plan);
                            const displayPrice = plan.originalPriceINR === 0 ? 'Free' : formatPrice(plan.convertedPrice, userCurrency);

                            return (
                                <div
                                    key={plan.id}
                                    className={`plan-card ${isCurrent ? 'current-plan-card' : ''} ${plan.name === 'free' ? 'free-card' : ''}`}
                                >
                                    {plan.name !== 'free' && (
                                        <div className="popular-badge">
                                            {plan.billingPeriod === 'yearly' && '🔥 Best Value'}
                                            {plan.billingPeriod === 'quarterly' && '⭐ Popular'}
                                            {plan.billingPeriod === 'monthly' && '📈 Flexible'}
                                        </div>
                                    )}

                                    <div className="plan-header">
                                        <div className="plan-icon-large">
                                            {plan.name === 'free' ? '🆓' : '💎'}
                                        </div>
                                        <div className="plan-name-large">{plan.displayName}</div>
                                        <div className="plan-price-large">
                                            {displayPrice}
                                            {plan.originalPriceINR > 0 && (
                                                <span className="plan-period">/{plan.billingPeriod}</span>
                                            )}
                                        </div>
                                        {plan.originalPriceINR > 0 && plan.billingPeriod === 'yearly' && (
                                            <div className="savings-badge">Save 40%</div>
                                        )}
                                        {plan.originalPriceINR > 0 && plan.billingPeriod === 'quarterly' && (
                                            <div className="savings-badge">Save 20%</div>
                                        )}
                                        {userCurrency !== 'INR' && plan.originalPriceINR > 0 && (
                                            <div className="original-price">
                                                <span className="strikethrough">
                                                    {formatPrice(plan.originalPriceINR, 'INR')}
                                                </span>
                                                <span className="converted-note">
                                                    ≈ {formatPrice(plan.convertedPrice, userCurrency)}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="plan-features">
                                        <div className="feature">
                                            <span>✓</span> Up to {plan.maxUsers} user{plan.maxUsers !== 1 ? 's' : ''}
                                        </div>
                                        <div className="feature">
                                            <span>✓</span> {plan.maxIncidentsPerMonth} incidents/month
                                        </div>
                                        <div className="feature">
                                            <span>✓</span> {plan.aiAnalysisLimit} AI analyses/month
                                        </div>
                                        <div className="feature">
                                            <span>✓</span> {plan.storageLimitMB} MB storage
                                        </div>
                                        {plan.features?.prioritySupport && (
                                            <div className="feature">✓ Priority Support</div>
                                        )}
                                        {plan.features?.apiAccess && (
                                            <div className="feature">✓ API Access</div>
                                        )}
                                        {plan.features?.advancedAnalytics && (
                                            <div className="feature">✓ Advanced Analytics</div>
                                        )}
                                    </div>

                                    <button
                                        className={`upgrade-btn ${isCurrent ? 'current-btn' : ''} ${plan.name === 'free' ? 'free-btn' : 'pro-btn'}`}
                                        onClick={() => handleUpgrade(plan)}
                                        disabled={isCurrent || processing || plan.name === 'free'}
                                    >
                                        {isCurrent ? 'Current Plan' : plan.name === 'free' ? 'Free Plan' : `Upgrade to ${plan.displayName}`}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionPlan;